import { Component, computed, inject, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HtmlInputType, ZiroTextConfig } from '../../../../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../../forms/ziro-forms-models';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../../../forms/fields/select/ziro-select.component';
import {
  BandwidthE911Dlr,
  BandwidthEmergencyCallingSettingsConfig,
  CallingType,
  EmergencyLocation,
  MicrosoftAttributes,
  MicrosoftDialPlanFieldConfig,
  PstnConnectivityType,
  SharedCallingExtension,
  SharedCallingRoutingPolicy,
  TeamsPhoneNumberAssignment,
  TeamsPhoneNumberAssignmentFieldConfig,
  TeamsVoicePolicies,
  TeamsVoicePoliciesFieldConfig,
} from '../../../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../../../forms/ziro-form-state.service';
import { ZiroIcons } from '../../../../../../shared/models/ziro-icons.enum';
import { forkJoin, Observable, of, Subscription, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  BandwidthDynamicFormData,
  BandwidthEmergencyCallingFormComponent,
} from './bandwidth-e911-dlrs-form/bandwidth-emergency-calling-form.component';
import { BandwidthE911DlrsResource } from '../../../../../shared/resources/bandwidth-e911-dlrs.resource';
import { cloneDeep, isEqual } from 'lodash';
import { e164ValidatorRegex } from '../../../../../../shared/e164-extension-validators-regex';
import { HelpdeskAdAttributesComponent } from './helpdesk-ad-attributes/helpdesk-ad-attributes.component';
import { HelpdeskEntraAttributesComponent } from './helpdesk-entra-attributes/helpdesk-entra-attributes.component';
import { delayWhen, map } from 'rxjs/operators';
import { DraasSharedExtensionsResource } from '../../../../../shared/resources/draas-shared-extensions.resource';
import { MicrosoftOnPremAdResource } from '../../../../../shared/resources/microsoft-on-prem-ad.resource';
import { MicrosoftEntraAttributesResource } from '../../../../../shared/resources/microsoft-entra-attributes.resource';
import { TeamsCallQueuesFormComponent } from './teams-call-queues/teams-call-queues-form.component';
import { MicrosoftPhoneNumberAssignmentsResource } from '../../../../../shared/resources/microsoft-phone-number-assignments.resource';
import { MicrosoftVoicePoliciesResource } from '../../../../../shared/resources/microsoft-voice-policies.resource';

export interface MsTeamsLine {
  lineUri: string;
  callingPolicy: string;
  teamsCallHoldPolicy: string;
  teamsCallParkPolicy: string;
  callingLineIdentity: string;
  tenantDialPlan: string;
  emergencyCallingPolicy: string;
  emergencyCallRoutingPolicy: string;
  onlineVoiceRoutingPolicy: string;
  onlineVoicemailPolicy: string;
  ipPhonePolicy: string;
  lineUriExtension: string;
  emergencyLocation: ZiroSelectOption;
}

@Component({
  selector: 'ziro-microsoft-teams-calling-form',
  templateUrl: './microsoft-teams-calling-form.component.html',
  styleUrls: ['./microsoft-teams-calling-form.component.scss'],
  standalone: false,
})
export class MicrosoftTeamsCallingFormComponent
  extends ZiroFormAbstractDirective<MsTeamsLine>
  implements OnInit, OnDestroy
{
  private _translateService = inject(TranslateService);
  private _microsoftPhoneNumberAssignmentsResource = inject(MicrosoftPhoneNumberAssignmentsResource);
  private _microsoftVoicePoliciesResource = inject(MicrosoftVoicePoliciesResource);
  private _bandwidthE911DlrsResource = inject(BandwidthE911DlrsResource);
  private _draasSharedExtensionsResource = inject(DraasSharedExtensionsResource);
  private _microsoftOnPremAdResource = inject(MicrosoftOnPremAdResource);
  private _microsoftEntraAttributesResource = inject(MicrosoftEntraAttributesResource);
  protected _ziroFormStateService: ZiroFormStateService;

  readonly tenantId = input<string>();
  readonly phoneNumberAssignmentFieldConfig = input<TeamsPhoneNumberAssignmentFieldConfig>();
  readonly voicePoliciesFieldConfig = input<TeamsVoicePoliciesFieldConfig>();
  readonly teamsLine = input<TeamsPhoneNumberAssignment>();
  readonly voicePolicies = input<TeamsVoicePolicies>();
  readonly upn = input<string>();
  readonly onPremUpn = input<string>();
  readonly lineUri = input<string>();
  readonly bandwidthFieldConfig = input<BandwidthEmergencyCallingSettingsConfig>();
  readonly bandwidthEntity = input<BandwidthDynamicFormData>();
  readonly dialPlanFieldConfig = input<MicrosoftDialPlanFieldConfig>();
  readonly initialBandwidthEndpointId = input<string>();
  readonly isExtensionChanged = input<boolean>();
  readonly isBandwidthConfiguredWithoutE911Dlr = input<boolean>();
  readonly isResourceAccount = input<boolean>();
  readonly isAdWriteDownConfigured = input<boolean>();
  isEntraWriteDownConfigured = input<boolean>();
  readonly isZpcSyncWarningPresent = input<boolean>();
  readonly mainNumber = input<string>();
  /** When true on an extension DPG, persist line URI as main number with no extension (#22934). */
  readonly isMainNumberSelected = input<boolean>(false);
  readonly initialZiroDraasSharedCallingExtension = input<SharedCallingExtension>();
  readonly initialLineUri = input<string>();
  readonly initialLineUriExtension = input<string>();
  readonly initialDialPlanGroup = input<MicrosoftDialPlanFieldConfig>();
  readonly sharedCallingPolicy = input<SharedCallingRoutingPolicy>();
  readonly isExistingSharedCalling = input<boolean>();
  readonly initialExistingNumber = input<string>();

  @ViewChild(BandwidthEmergencyCallingFormComponent)
  bandwidthEmergencyCallingFormComponent: BandwidthEmergencyCallingFormComponent;
  @ViewChild(HelpdeskAdAttributesComponent) helpdeskAdAttributesComponent: HelpdeskAdAttributesComponent;
  @ViewChild(HelpdeskEntraAttributesComponent) helpdeskEntraAttributesComponent: HelpdeskEntraAttributesComponent;
  @ViewChild(TeamsCallQueuesFormComponent) callQueuesFormComponent: TeamsCallQueuesFormComponent;

  // True if the Teams Call Settings section has at least one visible field to display
  readonly hasTeamsCallSettingsFieldsToShow = computed(() => {
    if (!this.isResourceAccount()) {
      return true;
    }
    const voicePolicies = this.voicePoliciesFieldConfig();
    const phoneNumberAssignment = this.phoneNumberAssignmentFieldConfig();
    return !!(
      voicePolicies?.callingPolicy?.show ||
      voicePolicies?.teamsCallHoldPolicy?.show ||
      voicePolicies?.teamsCallParkPolicy?.show ||
      voicePolicies?.callingLineIdentity?.show ||
      voicePolicies?.tenantDialPlan?.show ||
      voicePolicies?.emergencyCallingPolicy?.show ||
      voicePolicies?.emergencyCallRoutingPolicy?.show ||
      voicePolicies?.onlineVoiceRoutingPolicy?.show ||
      voicePolicies?.onlineVoicemailPolicy?.show ||
      voicePolicies?.ipPhonePolicy?.show ||
      phoneNumberAssignment?.emergencyLocation?.show ||
      phoneNumberAssignment?.lineUriExtension?.show
    );
  });

  formConfig: ZiroFormConfig;
  ziroIcons = ZiroIcons;
  isExistingTeamsCalling = false;
  isExistingLineUri = false;
  isBandwidthFormValid: boolean;
  lineUriExtensionForDedicatedDid: string;

  private _subscription = new Subscription();

  ngOnInit(): void {
    this._initFormConfig();
    this.isExistingTeamsCalling = !!this.teamsLine() || this.isExistingSharedCalling();
    this.isExistingLineUri = !!this.teamsLine();

    const teamsLine = this.teamsLine();
    this.lineUriExtensionForDedicatedDid = teamsLine?.lineUriExtension
      ? teamsLine.lineUriExtension
      : this.phoneNumberAssignmentFieldConfig()?.lineUriExtension.defaultValue;
    this._setUpdateEntity();

    const formSubmitSub = this._validateAndSubmitSource.subscribe(() => {
      if (this.bandwidthFieldConfig()) {
        this.bandwidthEmergencyCallingFormComponent._validateAndSubmitSource.next(true);
      }
      this.isBandwidthFormValid = this.bandwidthFieldConfig()
        ? this.bandwidthEmergencyCallingFormComponent.isFormValid()
        : true;
    });
    this._subscription.add(formSubmitSub);
    const updateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (cloneDeep(update.new?.lineUriExtension) !== cloneDeep(update.old?.lineUriExtension)) {
        this.handleExtensionUpdate(update.new.lineUriExtension);
      }
    });
    this._subscription.add(updateSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  isValidExtension(viewValue: string): boolean {
    return /^[0-9]{1,8}$/i.test(viewValue);
  }

  isPhoneNumber(viewValue: string): boolean {
    return e164ValidatorRegex.test(viewValue);
  }

  lineUriFormatValidation(viewValue: string, type: string): boolean {
    if (!viewValue) {
      return true;
    }
    return type === 'extension' ? this.isValidExtension(viewValue) : this.isPhoneNumber(viewValue);
  }

  handleExtensionUpdate(newValue: string): void {
    const lineUriExtensionField = this.fieldComponents.find((comp) => comp.fieldId() === 'lineUriExtension');
    const isExtensionValid = lineUriExtensionField?.validationState === ZiroFormsValidationState.VALID;
    if (newValue.length && this.isAdWriteDownConfigured() && !this.isZpcSyncWarningPresent() && isExtensionValid) {
      this.helpdeskAdAttributesComponent?.init(newValue);
    }
    if (newValue.length && this.isEntraWriteDownConfigured() && isExtensionValid) {
      this.helpdeskEntraAttributesComponent?.init(newValue);
    }
  }

  areAllFormsValid(): boolean {
    const isAdValid =
      this.isAdWriteDownConfigured() && !this.isZpcSyncWarningPresent() && !!this.helpdeskAdAttributesComponent
        ? this.helpdeskAdAttributesComponent.isFormValid()
        : true;
    const isEntraValid =
      this.isEntraWriteDownConfigured() && !!this.helpdeskEntraAttributesComponent
        ? this.helpdeskEntraAttributesComponent.isFormValid()
        : true;
    const isBandwidthValid = this.isBandwidthFormValid ?? true;
    return this.isFormValid() && isAdValid && isEntraValid && isBandwidthValid;
  }

  performSave(): Observable<void> {
    return this._saveTeamsCalling();
  }

  triggerValidation(): void {
    this._validateAndSubmitSource.next(true);
    this.helpdeskAdAttributesComponent?.adAttributeForm?._validateAndSubmitSource.next(true);
    this.helpdeskEntraAttributesComponent?.entraAttributeForm?._validateAndSubmitSource.next(true);
  }

  protected submit(): Observable<void> {
    return of(null);
  }

  private _setUpdateEntity(): void {
    if (this.isExistingTeamsCalling) {
      const teamsVoicePolicies = this.voicePolicies();
      const teamsLine = this.teamsLine();
      let existingLineUri = this._isDialPlanExtensionType() ? teamsLine?.lineUriExtension : teamsLine?.lineUri;
      if (!this.teamsLine() && this.mainNumber()) {
        existingLineUri = this.lineUri();
      }
      const updateEntity: MsTeamsLine = {
        lineUriExtension: teamsLine?.lineUriExtension,
        lineUri: existingLineUri,
        onlineVoiceRoutingPolicy: teamsVoicePolicies.onlineVoiceRoutingPolicy,
        callingPolicy: teamsVoicePolicies.callingPolicy,
        teamsCallHoldPolicy: teamsVoicePolicies.teamsCallHoldPolicy,
        teamsCallParkPolicy: teamsVoicePolicies.teamsCallParkPolicy,
        callingLineIdentity: teamsVoicePolicies.callingLineIdentity,
        tenantDialPlan: teamsVoicePolicies.tenantDialPlan,
        emergencyCallingPolicy: teamsVoicePolicies.emergencyCallingPolicy,
        emergencyCallRoutingPolicy: teamsVoicePolicies.emergencyCallRoutingPolicy,
        onlineVoicemailPolicy: teamsVoicePolicies.onlineVoicemailPolicy,
        ipPhonePolicy: teamsVoicePolicies.ipPhonePolicy,
        emergencyLocation: teamsLine ? this._emergencyLocationToSelectOption(teamsLine.emergencyLocation) : null,
      };
      this.entitySource.next({ ...updateEntity });
    } else {
      const phoneNumberAssignmentFieldConfig = this.phoneNumberAssignmentFieldConfig();
      const voicePoliciesFieldConfig = this.voicePoliciesFieldConfig();
      const newEntity: MsTeamsLine = {
        lineUri: this.lineUri(),
        callingPolicy: voicePoliciesFieldConfig?.callingPolicy?.defaultValue,
        teamsCallHoldPolicy: voicePoliciesFieldConfig?.teamsCallHoldPolicy?.defaultValue,
        teamsCallParkPolicy: voicePoliciesFieldConfig?.teamsCallParkPolicy?.defaultValue,
        callingLineIdentity: voicePoliciesFieldConfig?.callingLineIdentity?.defaultValue,
        tenantDialPlan: voicePoliciesFieldConfig?.tenantDialPlan?.defaultValue,
        emergencyCallingPolicy: voicePoliciesFieldConfig?.emergencyCallingPolicy?.defaultValue,
        emergencyCallRoutingPolicy: voicePoliciesFieldConfig?.emergencyCallRoutingPolicy?.defaultValue,
        onlineVoiceRoutingPolicy: voicePoliciesFieldConfig?.onlineVoiceRoutingPolicy?.defaultValue,
        onlineVoicemailPolicy: voicePoliciesFieldConfig?.onlineVoicemailPolicy?.defaultValue,
        ipPhonePolicy: voicePoliciesFieldConfig?.ipPhonePolicy?.defaultValue,
        lineUriExtension: phoneNumberAssignmentFieldConfig?.lineUriExtension?.defaultValue,
        emergencyLocation: this._emergencyLocationToSelectOption(
          phoneNumberAssignmentFieldConfig?.emergencyLocation?.defaultValue,
        ),
      };
      this.entitySource.next({ ...newEntity });
    }
  }

  private _initFormConfig(): void {
    const voicePoliciesFieldConfig = this.voicePoliciesFieldConfig();
    this.formConfig = {
      fields: {
        callingPolicy: {
          label: 'tkey;shared.model.microsoft_teams.calling_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.callingPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-calling-policy',
          required: () => this.voicePoliciesFieldConfig()?.callingPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.callingPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.callingPolicy?.defaultValue,
        },
        teamsCallHoldPolicy: {
          label: 'tkey;shared.model.microsoft_teams.teams_call_hold_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.teamsCallHoldPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-call-hold-policy',
          required: () => this.voicePoliciesFieldConfig()?.teamsCallHoldPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.teamsCallHoldPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.teamsCallHoldPolicy?.defaultValue,
        },
        teamsCallParkPolicy: {
          label: 'tkey;shared.model.microsoft_teams.teams_call_park_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.teamsCallParkPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-call-park-policy',
          required: () => this.voicePoliciesFieldConfig()?.teamsCallParkPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.teamsCallParkPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.teamsCallParkPolicy?.defaultValue,
        },
        callingLineIdentity: {
          label: 'tkey;shared.model.microsoft_teams.calling_line_identity.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.callingLineIdentity?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-calling-line-identity-policy',
          required: () => this.voicePoliciesFieldConfig()?.callingLineIdentity?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.callingLineIdentity?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.callingLineIdentity?.defaultValue,
        },
        tenantDialPlan: {
          label: 'tkey;shared.model.microsoft_teams.dial_plan.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.tenantDialPlan?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-tenant-dial-plan',
          required: () => this.voicePoliciesFieldConfig()?.tenantDialPlan?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.tenantDialPlan?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.tenantDialPlan?.defaultValue,
        },
        emergencyCallingPolicy: {
          label: 'tkey;shared.model.microsoft_teams.emergency_calling_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.emergencyCallingPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-emergency-calling-policy',
          required: () => this.voicePoliciesFieldConfig()?.emergencyCallingPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.emergencyCallingPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.emergencyCallingPolicy?.defaultValue,
        },
        emergencyCallRoutingPolicy: {
          label: 'tkey;shared.model.microsoft_teams.emergency_call_routing_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.emergencyCallRoutingPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-emergency-call-routing-policy',
          required: () => this.voicePoliciesFieldConfig()?.emergencyCallRoutingPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.emergencyCallRoutingPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.emergencyCallRoutingPolicy?.defaultValue,
        },
        onlineVoiceRoutingPolicy: {
          label: 'tkey;shared.model.microsoft_teams.voice_routing_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.onlineVoiceRoutingPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-voice-routing-policy',
          required: () => this.voicePoliciesFieldConfig()?.onlineVoiceRoutingPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.onlineVoiceRoutingPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.onlineVoiceRoutingPolicy?.defaultValue,
        },
        onlineVoicemailPolicy: {
          label: 'tkey;shared.model.microsoft_teams.online_voicemail_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.onlineVoicemailPolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-online-voicemail-policy',
          required: () => this.voicePoliciesFieldConfig()?.onlineVoicemailPolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.onlineVoicemailPolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.onlineVoicemailPolicy?.defaultValue,
        },
        ipPhonePolicy: {
          label: 'tkey;shared.model.microsoft_teams.ip_phone_policy.label',
          componentConfig: new ZiroSelectConfig({
            options: voicePoliciesFieldConfig?.ipPhonePolicy?.possibleOptions,
          }),
          dataAutomation: 'microsoft-teams-ip-phone-policy',
          required: () => this.voicePoliciesFieldConfig()?.ipPhonePolicy?.required,
          hidden: () => !this.voicePoliciesFieldConfig()?.ipPhonePolicy?.show,
          defaultValue: () => this.voicePoliciesFieldConfig()?.ipPhonePolicy?.defaultValue,
        },
        emergencyLocation: {
          label: 'tkey;shared.model.microsoft_teams.emergency_location.label',
          componentConfig: new ZiroSelectConfig({
            options: this.phoneNumberAssignmentFieldConfig()?.emergencyLocation.possibleOptions.map((option) =>
              this._emergencyLocationToSelectOption(option),
            ),
          }),
          dataAutomation: 'microsoft-teams-emergency-location',
          required: () => this.phoneNumberAssignmentFieldConfig()?.emergencyLocation?.required,
          hidden: () => !this.phoneNumberAssignmentFieldConfig()?.emergencyLocation?.show,
          defaultValue: () =>
            this._emergencyLocationToSelectOption(
              this.phoneNumberAssignmentFieldConfig()?.emergencyLocation?.defaultValue,
            ),
        },
        lineUriExtension: {
          label: 'tkey;shared.model.microsoft_teams.on_prem.line.uri.extension',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          validation: [
            {
              validator: (value: string) => {
                return this.lineUriFormatValidation(value, 'extension')
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              message: this._translateService.instant('tkey;shared.model.microsoft_teams.validation.extension.message'),
            },
          ],
          dataAutomation: 'microsoft-teams-line-extension',
          required: () => this.phoneNumberAssignmentFieldConfig()?.lineUriExtension?.required,
          hidden: () => !this.phoneNumberAssignmentFieldConfig()?.lineUriExtension?.show,
          defaultValue: () => this.phoneNumberAssignmentFieldConfig()?.lineUriExtension?.defaultValue,
        },
      },
    } as ZiroFormConfig;
  }

  private _saveTeamsCalling(): Observable<void> {
    const submitObs: Observable<any>[] = [];

    const sharedCallingPolicy = this.sharedCallingPolicy();
    const upn = this.upn();
    // Microsoft Entra ID write-down validates that the user already has this phone number assignment, so its
    // submission is chained after this observable below rather than raced against it.
    let phoneNumberAssignmentObs: Observable<any> = of(null);
    if (!sharedCallingPolicy) {
      const teamsLine: TeamsPhoneNumberAssignment = {
        userPrincipalName: this.upn(),
        lineUri: this._getLineUri(),
        lineUriExtension: this._getLineUriExtension(),
        phoneNumberType: this.phoneNumberAssignmentFieldConfig().phoneNumberType,
        emergencyLocation: this.formData.emergencyLocation ? this.formData.emergencyLocation.value : null,
      };

      phoneNumberAssignmentObs = this.isExistingLineUri
        ? this._microsoftPhoneNumberAssignmentsResource.put(upn, teamsLine, this.tenantId())
        : this._microsoftPhoneNumberAssignmentsResource.post(upn, teamsLine, this.tenantId());
    }

    const voicePolicies: TeamsVoicePolicies = {
      userPrincipalName: upn,
      onlineVoiceRoutingPolicy: this.formData.onlineVoiceRoutingPolicy ? this.formData.onlineVoiceRoutingPolicy : '',
      callingPolicy: this.formData.callingPolicy ? this.formData.callingPolicy : '',
      teamsCallHoldPolicy: this.formData.teamsCallHoldPolicy ? this.formData.teamsCallHoldPolicy : '',
      teamsCallParkPolicy: this.formData.teamsCallParkPolicy ? this.formData.teamsCallParkPolicy : '',
      callingLineIdentity: this.formData.callingLineIdentity ? this.formData.callingLineIdentity : '',
      tenantDialPlan: this.formData.tenantDialPlan ? this.formData.tenantDialPlan : '',
      emergencyCallingPolicy: this.formData.emergencyCallingPolicy ? this.formData.emergencyCallingPolicy : '',
      emergencyCallRoutingPolicy: this.formData.emergencyCallRoutingPolicy
        ? this.formData.emergencyCallRoutingPolicy
        : '',
      onlineVoicemailPolicy: this.formData.onlineVoicemailPolicy ? this.formData.onlineVoicemailPolicy : '',
      ipPhonePolicy: this.formData.ipPhonePolicy ? this.formData.ipPhonePolicy : '',
      sharedCallRoutingPolicy: sharedCallingPolicy || {
        policyName: 'Global',
        resourceAccountUpn: '',
        resourceAccountDisplayName: '',
        resourceAccountLineUri: '',
      },
    };
    submitObs.push(
      // these need to be done sequentially!! otherwise enterpriseVoiceEnabled will be false and their calling won't work
      this._deleteExistingTeamsLineIfDowngradingToSharedCalling().pipe(
        switchMap(() =>
          this._microsoftVoicePoliciesResource.put(
            Number(this.tenantId()),
            this.upn(),
            voicePolicies,
            this.isResourceAccount(),
          ),
        ),
      ),
    );

    if (
      !!this.helpdeskAdAttributesComponent &&
      this.helpdeskAdAttributesComponent.isFormValid() &&
      this.isAdWriteDownConfigured()
    ) {
      submitObs.push(this.helpdeskAdAttributesComponent.submitAdAttributeForm());
    }

    const shouldSubmitEntraAttributes =
      !!this.helpdeskEntraAttributesComponent &&
      this.helpdeskEntraAttributesComponent.isFormValid() &&
      this.isEntraWriteDownConfigured();
    submitObs.push(
      phoneNumberAssignmentObs.pipe(
        switchMap(() =>
          shouldSubmitEntraAttributes ? this.helpdeskEntraAttributesComponent.submitEntraAttributeForm() : of(null),
        ),
      ),
    );

    const lineUri = this._getLineUri();
    const lineUriExtension = this._getLineUriExtension();
    const initialZiroDraasSharedCallingExtension = this.initialZiroDraasSharedCallingExtension();
    const initialLineUri = this.initialLineUri();
    if (
      initialZiroDraasSharedCallingExtension &&
      (initialZiroDraasSharedCallingExtension.extension !== lineUriExtension || initialLineUri !== lineUri)
    ) {
      submitObs.push(
        this._draasSharedExtensionsResource.deleteExtension(
          this.tenantId(),
          initialLineUri,
          initialZiroDraasSharedCallingExtension.extension,
        ),
      );
    }

    if (this._isDraasSharedMainNumberDialPlan() && !!lineUriExtension) {
      const draasExtensionObs = this._draasSharedExtensionsResource.getAllExtensions(this.tenantId(), lineUri).pipe(
        switchMap((e164Extensions) => {
          if (!e164Extensions.sharedExtensions.some((ext) => ext.extension === lineUriExtension)) {
            return this._draasSharedExtensionsResource.postExtension(this.tenantId(), lineUri, {
              sharedCallingExtension: lineUriExtension,
            });
          } else {
            return of(null);
          }
        }),
      );
      submitObs.push(draasExtensionObs);
    }

    if (!this.isResourceAccount() && !!this.callQueuesFormComponent && this.callQueuesFormComponent.isDataInitialized) {
      submitObs.push(this.callQueuesFormComponent.saveQueues());
    }

    const initialDialPlanGroup = this.initialDialPlanGroup();
    if (
      !this.dialPlanFieldConfig().onPremAdWriteDownEnabled &&
      initialDialPlanGroup?.onPremAdWriteDownEnabled &&
      !this.isZpcSyncWarningPresent() &&
      !!this.onPremUpn()
    ) {
      const emptyAttributesSub = this._microsoftOnPremAdResource
        .getAdAttributes(this.tenantId(), this.onPremUpn(), initialDialPlanGroup.id)
        .pipe(
          switchMap((existingAttribs: MicrosoftAttributes) => {
            if (existingAttribs.writeDownListJson.length) {
              return this._microsoftOnPremAdResource.putAdAttributes(
                this.tenantId(),
                this.onPremUpn(),
                this.initialDialPlanGroup().id,
                {
                  ...existingAttribs,
                  writeDownListJson: existingAttribs.writeDownListJson.map((attr) => {
                    return {
                      attributeName: attr.attributeName,
                      attributeValue: '',
                    };
                  }),
                },
              );
            } else {
              return of(null);
            }
          }),
        );
      submitObs.push(emptyAttributesSub);
    }

    if (
      !this.dialPlanFieldConfig().entraAttributeWriteDownEnabled &&
      initialDialPlanGroup?.entraAttributeWriteDownEnabled &&
      !this.onPremUpn()
    ) {
      const emptyEntraAttributesSub = this._microsoftEntraAttributesResource
        .getEntraAttributes(this.tenantId(), this.upn(), initialDialPlanGroup.id)
        .pipe(
          switchMap((existingAttribs: MicrosoftAttributes) => {
            if (existingAttribs.writeDownListJson.length) {
              return this._microsoftEntraAttributesResource.putEntraAttributes(
                this.tenantId(),
                this.upn(),
                initialDialPlanGroup.id,
                {
                  ...existingAttribs,
                  writeDownListJson: existingAttribs.writeDownListJson.map((attr) => {
                    return {
                      attributeName: attr.attributeName,
                      attributeValue: '',
                    };
                  }),
                },
              );
            } else {
              return of(null);
            }
          }),
        );
      submitObs.push(emptyEntraAttributesSub);
    }

    return forkJoin(submitObs).pipe(
      map(() => {}),
      delayWhen(() => {
        if (!this.bandwidthFieldConfig()) {
          return of(null);
        }
        const body: BandwidthE911Dlr = {
          id: this.bandwidthFieldConfig().dynamicGeolocationEndpointId.value,
          geolocation: this.bandwidthEmergencyCallingFormComponent.formData.dynamicGeolocations,
          callerName: this.bandwidthFieldConfig().dynamicGeolocationCallerName.value,
          callbackNumber: this.bandwidthFieldConfig().dynamicGeolocationCallbackNumber.value,
          preferredLanguage: this.bandwidthFieldConfig().dynamicGeolocationPreferredLanguage.defaultOption,
        };
        const isBandwidthConfiguredWithoutE911Dlr = this.isBandwidthConfiguredWithoutE911Dlr();
        const isExtensionChanged = this.isExtensionChanged();
        const initialBandwidthEndpointId = this.initialBandwidthEndpointId();
        if (!this.isExistingTeamsCalling || isBandwidthConfiguredWithoutE911Dlr) {
          // If this is a new line, simply perform a POST to create our Bandwidth Data
          return this._bandwidthE911DlrsResource.post(this.tenantId(), body);
        } else if (
          !isEqual(this.bandwidthEntity(), this.bandwidthEmergencyCallingFormComponent.formData) &&
          !isExtensionChanged
        ) {
          // If this is an existing line and the geolocation input value has changed but the
          // extension has not changed, perform a simple PUT to the endpoint ID because no DELETE is required
          return this._bandwidthE911DlrsResource.put(this.tenantId(), body);
        } else if (isExtensionChanged && !!initialBandwidthEndpointId && !isBandwidthConfiguredWithoutE911Dlr) {
          // If the extension has changed then we must DELETE the existing Bandwidth data and POST an updated set
          return this._bandwidthE911DlrsResource
            .delete(this.tenantId(), initialBandwidthEndpointId)
            .pipe(switchMap(() => this._bandwidthE911DlrsResource.post(this.tenantId(), body)));
        }
        return of(null);
      }),
    );
  }

  private _getLineUri(): string {
    if (this._isDialPlanExtensionType()) {
      return this.dialPlanFieldConfig().mainNumber || '';
    }
    return this.lineUri() || '';
  }

  private _getLineUriExtension(): string {
    if (this._isDialPlanExtensionType()) {
      if (this.isMainNumberSelected()) {
        return '';
      }
      return this.formData.lineUri ? this.formData.lineUri : '';
    }
    return this.formData.lineUriExtension ? this.formData.lineUriExtension : '';
  }

  private _isDialPlanExtensionType(): boolean {
    const dialPlanFieldConfig = this.dialPlanFieldConfig();
    return dialPlanFieldConfig && dialPlanFieldConfig.callingType === CallingType.EXTENSION;
  }

  private _isDraasSharedMainNumberDialPlan(): boolean {
    const ziroDraasPstnTypes = [PstnConnectivityType.ZIRO_DRAAS, PstnConnectivityType.ZIRO_DRAAS_BYOC];
    const dialPlanFieldConfig = this.dialPlanFieldConfig();
    return (
      dialPlanFieldConfig?.callingType === CallingType.EXTENSION &&
      ziroDraasPstnTypes.includes(dialPlanFieldConfig?.pstnConnectivityType)
    );
  }

  private _emergencyLocationToSelectOption(emergencyLocation: EmergencyLocation): ZiroSelectOption {
    if (!emergencyLocation) {
      return null;
    }
    return {
      label: emergencyLocation.description,
      value: emergencyLocation,
    };
  }

  private _deleteExistingTeamsLineIfDowngradingToSharedCalling(): Observable<void> {
    if (!this.sharedCallingPolicy() || !this.isExistingLineUri) {
      return of(null);
    }
    return this._microsoftPhoneNumberAssignmentsResource.delete(this.upn(), this.tenantId());
  }
}
